import type { TFunction } from 'i18next';
import { GitBranch, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { components } from '@/lib/api-types-administration';
import { useRoleText } from '../roles/role-text';

type RoleHolding = components['schemas']['RoleHolding'];

export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toLocaleUpperCase();
}

/** A person's initials, or an envelope for an invitation, which has no person yet. */
export function Avatar({
  name,
  invitation = false,
  muted = false,
  size = 36,
}: {
  readonly name: string;
  readonly invitation?: boolean;
  readonly muted?: boolean;
  readonly size?: 36 | 40 | 48;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'person-avatar',
        invitation && 'person-avatar-invitation',
        muted && 'person-avatar-muted',
      )}
      style={{ width: size, height: size }}
    >
      {invitation ? <Mail className="size-4" /> : initials(name)}
    </span>
  );
}

/** Whole days from now until `iso`, rounded up: an invitation that expires in 3 h "expires today". */
export function daysUntil(iso: string, now: Date = new Date()): number {
  const ms = new Date(iso).getTime() - now.getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

export function useExpiryText(): (expiresAt: string) => string {
  const { t } = useTranslation('administration');
  return (expiresAt) => {
    const days = daysUntil(expiresAt);
    if (days === 0) {
      return t('people.expiresToday');
    }
    if (days === 1) {
      return t('people.expiresTomorrow');
    }
    return t('people.expiresIn', { count: days });
  };
}

/**
 * Where an inherited role comes from: the department or group by its name when the server could
 * read it (`viaName`), and without one otherwise — never a blank where the name would be. `chip` is
 * the short form beside a role chip, `sheet` the sentence under a role in the sheet and the lists.
 */
export function inheritedFrom(
  t: TFunction<'administration'>,
  holding: { readonly via: 'direct' | 'department' | 'group'; readonly viaName?: string | null },
  form: 'chip' | 'sheet',
): string | null {
  const name = holding.viaName?.trim() ?? '';
  if (holding.via === 'department') {
    if (form === 'chip') {
      return name === '' ? t('people.viaDepartmentUnnamed') : t('people.viaDepartment', { name });
    }
    return name === ''
      ? t('person.holdingDepartmentUnnamed')
      : t('person.holdingDepartment', { name });
  }
  if (holding.via === 'group') {
    if (form === 'chip') {
      return name === '' ? t('people.viaGroupUnnamed') : t('people.viaGroup', { name });
    }
    return name === '' ? t('person.holdingGroupUnnamed') : t('person.holdingGroup', { name });
  }
  return null;
}

/** A role held here: a plain chip, or a dashed one with a branch for a role inherited from a department or group. */
export function RoleChip({ holding }: { readonly holding: RoleHolding }) {
  const { t } = useTranslation('administration');
  const { name } = useRoleText();
  const inherited = holding.via !== 'direct';
  const via = inheritedFrom(t, holding, 'chip');
  return (
    <span className={cn('role-chip', inherited && 'role-chip-inherited')} title={via ?? undefined}>
      {inherited ? <GitBranch aria-hidden="true" className="size-3.5" /> : null}
      {name(holding.role)}
      {via !== null ? <span className="sr-only"> ({via})</span> : null}
    </span>
  );
}
